import React, { useCallback } from "react";
import Select, { GroupBase, StylesConfig } from "react-select";

import { IOptionSelect, ISelectViewProps } from "@/types/customSelectTypes";

import { useTheme } from "@/providers/ThemeProvider";
import { RADIUS } from "@/config/radius";

/** Flag image dimensions in the dropdown (px) */
const FLAG_IMAGE_SIZE = 20;

/** Gap between flag image and label text (px) */
const FLAG_IMAGE_GAP = 8;

/**
 * Renders a flag image alongside the option label when imageUrl is present.
 * Used for both the dropdown menu and the selected value display.
 * @param option - The select option to render
 */
/** Flag-label wrapper span style (web). */
const FLAG_LABEL_SPAN_STYLE: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
};

/** Flag image style (web). */
const FLAG_IMAGE_STYLE: React.CSSProperties = {
  marginRight: FLAG_IMAGE_GAP,
  flexShrink: 0,
};

const formatOptionLabel = (option: IOptionSelect): React.ReactNode => (
  // eslint-disable-next-line no-restricted-syntax -- raw DOM span in react-select's web-only formatOptionLabel render prop (menu portaled to document.body); not an RN primitive — RNW Box would change react-select option/value layout
  <span style={FLAG_LABEL_SPAN_STYLE}>
    {option.imageUrl ? (
      <img
        src={option.imageUrl}
        alt=""
        width={FLAG_IMAGE_SIZE}
        height={FLAG_IMAGE_SIZE}
        // eslint-disable-next-line no-restricted-syntax -- raw DOM img (src/alt) in react-select's portaled render prop; RNW Image is a different-behavior primitive (rule 2) with distinct DOM/load semantics
        style={FLAG_IMAGE_STYLE}
      />
    ) : null}
    {option.label}
  </span>
);

/**
 * Creates themed styles for the react-select dropdown
 * Matches Gluestack Input component visual standards:
 * borderColor uses background-300 tokens, 2px border, 48px height, 16px font
 * @param isDark - Whether dark theme is active
 */
const createCustomStyles = (
  isDark: boolean
): StylesConfig<IOptionSelect, false, GroupBase<IOptionSelect>> => {
  // Use Gluestack background-300 tokens to match the Input component border color:
  // light: rgb(213,212,212) = #D5D4D4 | dark: rgb(116,116,116) = #747474
  const borderColor = isDark ? "#747474" : "#D5D4D4";
  // Match Gluestack background-50 dark (#272625) and white for light
  const backgroundColor = isDark ? "#272625" : "#ffffff";
  const textColor = isDark ? "#F9FAFB" : "#111827";
  // Hover/selected use background-100 tokens: light #F6F6F6, dark #414040
  const hoverColor = isDark ? "#414040" : "#F6F6F6";
  const selectedColor = isDark ? "#414040" : "#F6F6F6";

  return {
    control: base => ({
      ...base,
      backgroundColor,
      // Match Gluestack Input: rounded-sm (4px), h-12 (48px), 2px border, 16px text
      borderRadius: RADIUS.sm,
      border: `2px solid ${borderColor}`,
      height: "48px",
      fontSize: "16px",
      boxShadow: "none",
      "&:hover": { borderColor },
      display: "flex",
      flex: 1,
      width: "100%",
    }),
    menu: base => ({
      ...base,
      backgroundColor,
      border: `1px solid ${borderColor}`,
      borderRadius: RADIUS.sm,
      zIndex: 9999,
    }),
    indicatorsContainer: base => ({
      ...base,
      borderLeft: "none",
    }),
    option: (base, { isFocused, isSelected }) => ({
      ...base,
      backgroundColor: isSelected
        ? selectedColor
        : isFocused
          ? hoverColor
          : backgroundColor,
      color: textColor,
      cursor: "pointer",
      fontSize: "16px",
    }),
    singleValue: base => ({
      ...base,
      fontFamily: "Satoshi",
      color: textColor,
    }),
    input: base => ({
      ...base,
      color: textColor,
    }),
    dropdownIndicator: base => ({
      ...base,
      color: textColor,
    }),
    menuList: base => ({
      ...base,
    }),
  };
};
/**
 * Renders a themed select dropdown for web platform
 * @param root0 - The component props
 * @param root0.options - Array of selectable options to display in dropdown
 * @param root0.selected - Currently selected option object
 * @param root0.placeholder - Text to show when no option is selected
 * @param root0.onChangeSelect - Callback invoked when user selects an option with the selected option object
 */
const CustomSelectView = ({
  options,
  selected,
  placeholder,
  onChangeSelect,
}: ISelectViewProps) => {
  const { resolvedTheme } = useTheme();
  const customStyles = createCustomStyles(resolvedTheme === "dark");

  const handleChange = useCallback(
    (value: IOptionSelect | null) => {
      onChangeSelect(value as IOptionSelect);
    },
    [onChangeSelect]
  );

  return (
    <Select
      options={options}
      placeholder={placeholder}
      value={selected}
      onChange={handleChange}
      styles={customStyles}
      formatOptionLabel={formatOptionLabel}
      menuPortalTarget={document.body}
    />
  );
};

export default CustomSelectView;
